import {useCallback, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router";
import ConfirmDialog from "@/components/forms/ConfirmDialog.tsx";
import DetailPaneState from "@/components/management/DetailPaneState.tsx";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import {useUnsavedChangesGuard} from "@/hooks/useUnsavedChangesGuard.ts";
import DungeonGeometryForm from "@/dungeons/admin/form/DungeonGeometryForm.tsx";
import DungeonBasicsForm from "@/dungeons/admin/form/DungeonBasicsForm.tsx";
import {useDungeonQuery, useSaveDungeonBasicsMutation, useSaveDungeonGeometryMutation} from "@/dungeons/DungeonQueries.ts";
import {Dungeon} from "@/dungeons/model/Dungeon.ts";

export default function DungeonEditor({mode}: {mode: "create" | "edit" | "geometry"}) {
    const {id} = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const {campaignPath} = useCampaign();
    const [dirty, setDirty] = useState(false);
    const query = useDungeonQuery(mode !== "create" ? id : undefined);
    const basicsMutation = useSaveDungeonBasicsMutation();
    const geometryMutation = useSaveDungeonGeometryMutation(id ?? "");
    const {run: guardNavigation, dialogProps} = useUnsavedChangesGuard(dirty, mode === "create" ? {
        title: "Neuen Dungeon verwerfen?",
        description: "Die bereits vorgenommenen Eingaben und temporären Bilder gehen verloren.",
        confirm: "Eingaben verwerfen",
    } : undefined);
    const continueEditing = useCallback((saved: Dungeon) => {
        setDirty(false);
        if (mode === "create") {
            navigate({pathname: campaignPath(`dungeons/${saved.id}`), search: location.search}, {replace: true});
        }
    }, [campaignPath, location.search, mode, navigate]);
    const cancel = useCallback(() => guardNavigation(() => {
        setDirty(false);
        const target = mode !== "create" && id ? `dungeons/${id}` : "dungeons";
        navigate({pathname: campaignPath(target), search: location.search}, {replace: true});
    }), [campaignPath, guardNavigation, id, location.search, mode, navigate]);
    if (mode !== "create" && query.isPending) return <DetailPaneState title="Dungeon wird geladen…" loading/>;
    if (mode !== "create" && query.isError) return <DetailPaneState title="Dungeon konnte nicht geladen werden" onRetry={() => void query.refetch()}/>;
    if (mode !== "create" && !query.data) return <DetailPaneState title="Dungeon nicht verfügbar"/>;
    return <>
        <title>{mode === "create" ? "Neuer Dungeon | DireDawn" : `${query.data!.name} ${mode === "geometry" ? "im Editor" : "bearbeiten"} | DireDawn`}</title>
        <ConfirmDialog {...dialogProps}/>
        {mode === "geometry"
            ? <DungeonGeometryForm key={query.data!.id} dungeon={query.data!}
                onSubmit={geometryMutation.mutateAsync} onSaved={continueEditing}
                onCancel={cancel} onDirtyChange={setDirty}/>
            : <DungeonBasicsForm key={query.data?.id ?? "new"} dungeon={query.data}
                onSubmit={basicsMutation.mutateAsync} onSaved={continueEditing}
                onCancel={cancel} onDirtyChange={setDirty}/>}
    </>;
}
